/**
 * understandingIntent.js — classifies a learner search-bar query
 * into one of two operator-locked intent types.
 *
 * Operator doctrine (May 19, 2026):
 *
 *   1. NAVIGATION/SEARCH intent — "book a lesson", "payments",
 *      "homework", "upload video". Routes to pages.
 *
 *   2. UNDERSTANDING/SUPPORT intent — "I'm worried about
 *      roundabouts", "I'm not good at junctions", "I panic at
 *      traffic lights", "I don't understand crossroads", "this
 *      stresses me out". MUST NOT route into formal progression,
 *      modules, levels, quizzes or scoring. MUST open a calm
 *      Understanding Card.
 *
 *      "Understanding before teaching."
 *
 * Public API:
 *
 *   classifyLearnerIntent(query) →
 *     { kind: "understanding", topic: "roundabouts"|"<other>"|null,
 *       phrase: "<the matched concern phrase>", raw: "<original>" }
 *     OR
 *     { kind: "navigation", route: "/some/path", raw: "<original>" }
 *     OR
 *     { kind: "fallback", raw: "<original>" }   // empty / unmatched
 */

// ── Understanding patterns ──────────────────────────────────────────
//
// Concern phrases — if ANY of these match, the query is an
// understanding intent. Order doesn't matter for matching, only for
// the "phrase" we surface back to the learner.
// NOTE: `i(?:\s+am|['’]?m)` accepts:
//   • straight ASCII apostrophe:  "I'm ..."
//   • curly Unicode apostrophe:   "I’m ..."   (iOS/macOS/Windows autocorrect default — U+2019)
//   • spelled-out form:           "I am ..."
// The straight-only `\bi'?m\b` variant silently missed every mobile learner
// whose keyboard substitutes smart quotes.
const CONCERN_PATTERNS = [
  /\bi(?:\s+am|['’]?m)\s+(?:worried|nervous|anxious|scared|afraid|terrified)\s+(?:about|of|by)?\b/i,
  /\bi(?:\s+am|['’]?m)\s+not\s+(?:good|great|confident)\s+(?:at|with)\b/i,
  /\bi\s+(?:don'?t|don['’]t|can'?t|can['’]t)\s+(?:understand|get|grasp)\b/i,
  /\bi\s+(?:panic|freeze|stress|struggle|stuggle)\s+(?:at|with|on|over|about)?\b/i,
  /\bthis\s+(?:stresses|scares|worries|frightens)\s+me\b/i,
  /\b(?:hate|dread|fear)\s+(?:doing|going on|driving on|the)?\b/i,
  /\bi\s+keep\s+(?:getting|making)\s+(?:it|them|this)?\s*wrong\b/i,
  /\bi(?:\s+am|['’]?m)\s+(?:confused|overwhelmed|lost)\b/i,
  /\bhelp\s+me\s+(?:understand|with)\b/i,
];

// Topic detection — these are the calm understanding topics we
// currently have analogies for. A query without a known topic still
// resolves as an understanding intent — the card will surface a
// calm generic acknowledgement instead.
const TOPIC_PATTERNS = [
  { id: "roundabouts",       match: /\b(roundabout|round[- ]?abouts?)\b/i },
  { id: "junctions",         match: /\b(junction|junctions|t[- ]?junction)\b/i },
  { id: "crossroads",        match: /\b(crossroad|cross[- ]?roads?)\b/i },
  { id: "traffic_lights",    match: /\b(traffic[- ]?lights?|red light|amber light)\b/i },
  { id: "motorways",         match: /\b(motorway|motorways|m[- ]?way)\b/i },
  { id: "dual_carriageways", match: /\b(dual[- ]?carriageways?|dual[- ]?carriage[- ]?ways?)\b/i },
  { id: "meeting_traffic",   match: /\b(meeting traffic|narrow road|two[- ]?way road)\b/i },
  { id: "parking",           match: /\b(parking|reverse park|bay park|parallel park)\b/i },
  { id: "hill_starts",       match: /\b(hill start|hill[- ]?starts|uphill start)\b/i },
];

// ── Navigation patterns ──────────────────────────────────────────────
//
// Mirrored from the existing LearnerHomeIntent INTENT_ROUTES so the
// two surfaces stay in sync. Order matters — more specific first.
const NAV_ROUTES = [
  { match: /\b(book|booking|lesson slot|reserve)\b/i,            route: "/learn/book" },
  { match: /\b(waitlist|last[- ]minute)\b/i,                      route: "/learn/last-minute" },
  { match: /\b(payment|invoice|money|stripe|refund)\b/i,         route: "/money" },
  { match: /\b(homework|reading list)\b/i,                       route: "/learn/homework" },
  { match: /\bupload\s+(?:video|clip|footage|review)\b/i,        route: "/start-review" },
  { match: /\b(how am i (doing|going)|progress|passport)\b/i,    route: "/learn/progress" },
  { match: /\b(latest review|my review|see my review|ai review)\b/i, route: "/learn/ai-review-requests" },
  { match: /\b(mock test|mocktest|practice test)\b/i,            route: "/learn/highway-code" },
  // Containment doctrine (May 26, 2026): the previous generic
  // rule `/\b(learn|learning|lesson|study|module|theory)\b/`
  // routed every casual query into /learn/learning, which then
  // exposed the 45-item topics-ahead grid. Removed. Casual
  // queries now fall through to the calm fallback (which routes
  // to /learn/overview where the slot engine drives one
  // contextual item at a time). Concern phrases still win first
  // and open the calm UnderstandingCard.
  { match: /\b(activity|recent|history)\b/i,                     route: "/operations" },
];

/**
 * classifyLearnerIntent — operator-locked classifier.
 *
 *  Resolution order:
 *    1. Empty / whitespace  → kind: "fallback"
 *    2. Concern phrase hit  → kind: "understanding"
 *                             (+ topic if a known one is named)
 *    3. Navigation hit      → kind: "navigation"
 *    4. Otherwise           → kind: "fallback"
 *
 *  Doctrine pin: understanding ALWAYS wins over navigation. A
 *  learner who says "I'm worried about booking a lesson" must NOT
 *  be routed straight to the booking page — they need
 *  acknowledgement first.
 */
export function classifyLearnerIntent(query) {
  const raw = String(query || "");
  const q = raw.trim();
  if (!q) return { kind: "fallback", raw };

  // 2. Understanding wins.
  let matchedPhrase = null;
  for (const pat of CONCERN_PATTERNS) {
    const m = q.match(pat);
    if (m) { matchedPhrase = m[0]; break; }
  }
  if (matchedPhrase) {
    let topic = null;
    for (const t of TOPIC_PATTERNS) {
      if (t.match.test(q)) { topic = t.id; break; }
    }
    return { kind: "understanding", topic, phrase: matchedPhrase, raw };
  }

  // 3. Navigation.
  for (const { match, route } of NAV_ROUTES) {
    if (match.test(q)) return { kind: "navigation", route, raw };
  }

  // 4. Fallback.
  return { kind: "fallback", raw };
}

export const _INTERNAL = {
  CONCERN_PATTERNS,
  TOPIC_PATTERNS,
  NAV_ROUTES,
};
